import type {
	AgentEvent,
	AttachedFile,
	ProjectProposal,
	ReviewRequestedPayload,
	ReconciliationQuestion,
	StreamCallbacks,
} from './types';
import {SimpleUUID, AGENT_URL} from './constants';

enum StateDeltaType {
	RefiningStarted = 'refining_started',
	Proposal = 'proposal',
	Warnings = 'warnings',
	Committed = 'committed',
	ClassificationBlocked = 'classification_blocked',
	PlaceholderProgress = 'placeholder_progress',
}

export async function parseSSEStream(response: Response, callbacks: StreamCallbacks, signal?: AbortSignal): Promise<void> {
	if (!response.body) return;
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let pendingEventType: string | null = null;

	// Cancel the reader immediately when the signal fires so that a blocked
	// reader.read() (e.g. waiting for the next SSE chunk) unblocks right away.
	const abortHandler = () => {
		reader.cancel().catch(() => undefined);
	};
	signal?.addEventListener('abort', abortHandler);

	try {
		for (;;) {
			if (signal?.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}
			const {done, value} = await reader.read();
			if (done) {
				if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
				break;
			}

			buffer += decoder.decode(value, {stream: true});
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';

			for (const rawLine of lines) {
				const line = rawLine.trimEnd();

				if (line.startsWith('event: ')) {
					pendingEventType = line.slice(7).trim();
					continue;
				}

				if (line.startsWith('data: ')) {
					const dataStr = line.slice(6).trim();
					let parsed: AgentEvent;
					try {
						parsed = JSON.parse(dataStr) as AgentEvent;
					} catch {
						if (pendingEventType === 'REVIEW_REQUESTED' || pendingEventType === 'RECONCILIATION_QUESTION') {
							callbacks.onError('A processing error occurred. Please try again.');
						}
						pendingEventType = null;
						continue;
					}

					// Custom STATE_DELTA event
					if (pendingEventType === 'STATE_DELTA') {
						if (parsed.type === StateDeltaType.RefiningStarted) {
							callbacks.onRefiningStarted();
						} else if (parsed.type === StateDeltaType.Proposal) {
							callbacks.onProposal(parsed.proposal ?? (parsed as unknown as ProjectProposal));
						} else if (parsed.type === StateDeltaType.Warnings) {
							callbacks.onWarnings(parsed.warnings ?? []);
						} else if (parsed.type === StateDeltaType.Committed) {
							callbacks.onCommitted(
								parsed.project_id ?? null,
								parsed.relay_project_id,
								parsed.deliverable_count,
								parsed.role_count
							);
						} else if (parsed.type === StateDeltaType.ClassificationBlocked) {
							callbacks.onClassificationBlocked?.(parsed.block_reason);
						} else if (parsed.type === StateDeltaType.PlaceholderProgress && parsed.placeholders) {
							callbacks.onPlaceholderProgress?.(parsed.placeholders);
						}
						pendingEventType = null;
						continue;
					}

					if (pendingEventType === 'REVIEW_REQUESTED') {
						const payload = parsed as unknown as ReviewRequestedPayload;
						if (payload.proposal) {
							callbacks.onReviewRequested?.(payload);
						} else {
							// Backend sent REVIEW_REQUESTED without a proposal — surface as an error
							// so the user sees a message rather than being stuck on the checklist
							callbacks.onError(
								'The project could not be extracted from the document. Please try again or switch to manual setup.'
							);
						}
						pendingEventType = null;
						continue;
					}

					if (pendingEventType === 'RECONCILIATION_QUESTION') {
						callbacks.onReconciliationQuestion?.(parsed as unknown as ReconciliationQuestion);
						pendingEventType = null;
						continue;
					}

					pendingEventType = null;

					// Standard AG-UI events
					// ag_ui serializes with by_alias=True + alias_generator=to_camel → camelCase field names
					const eventType: string = parsed.type ?? '';

					switch (eventType) {
						case 'TEXT_MESSAGE_START': {
							const id = parsed.messageId ?? SimpleUUID();
							callbacks.onNewAssistantMessage(id);
							break;
						}
						case 'TEXT_MESSAGE_CONTENT': {
							const id = parsed.messageId ?? '';
							callbacks.onText(id, parsed.delta ?? '');
							break;
						}
						case 'TEXT_MESSAGE_END': {
							callbacks.onMessageStreamEnd(parsed.messageId ?? '');
							break;
						}
						case 'TOOL_CALL_START': {
							const toolCallId = parsed.toolCallId ?? SimpleUUID();
							const toolName = parsed.toolCallName ?? 'tool';
							callbacks.onToolCall(toolCallId, toolName);
							break;
						}
						case 'TOOL_CALL_RESULT': {
							callbacks.onToolResult(parsed.toolCallId ?? '');
							break;
						}
						case 'RUN_FINISHED': {
							callbacks.onFinished();
							break;
						}
						case 'RUN_ERROR': {
							callbacks.onError(parsed.message ?? 'An unknown error occurred');
							break;
						}
						case 'STEP_STARTED': {
							callbacks.onStepStarted?.(parsed.stepName ?? parsed.step ?? '');
							break;
						}
						case 'STEP_FINISHED': {
							callbacks.onStepFinished?.(parsed.stepName ?? parsed.step ?? '');
							break;
						}
						default:
							break;
					}
				}
			}
		}
	} finally {
		signal?.removeEventListener('abort', abortHandler);
		reader.releaseLock();
	}
}

export async function invokeAgent(
	userText: string,
	threadId: string,
	apiKey: string,
	callbacks: StreamCallbacks,
	signal: AbortSignal,
	attachment?: AttachedFile,
	personId?: number,
	companyId?: string,
	companyName?: string
): Promise<void> {
	const runId = SimpleUUID();
	const msgId = SimpleUUID();

	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (apiKey) headers['x-forecast-api-key'] = apiKey;
	if (personId != null) headers['x-person-id'] = String(personId);
	// Tenant headers for telemetry. Omit entirely when unavailable —
	// the gateway treats absent and empty headers differently.
	if (companyId) headers['X-Forecast-Company-Id'] = companyId;
	if (companyName) headers['X-Forecast-Company-Name'] = companyName;
	headers['x-agent-id'] = 'project_planning';

	const response = await fetch(`${AGENT_URL}/invocations`, {
		method: 'POST',
		headers,
		credentials: 'include',
		signal,
		body: JSON.stringify({
			api_key: apiKey,
			thread_id: threadId,
			run_id: runId,
			state: {},
			messages: [{id: msgId, role: 'user', content: userText}],
			tools: [],
			context: [],
			forwardedProps: {agent_id: 'project_planning'},
			...(attachment && {
				attachments: [
					{
						filename: attachment.filename,
						mime_type: attachment.mimeType,
						data: attachment.dataBase64,
					},
				],
			}),
		}),
	});

	if (!response.ok) {
		const text = await response.text().catch(() => '');
		callbacks.onError(`Server error ${response.status}: ${text.slice(0, 200)}`);
		return;
	}

	await parseSSEStream(response, callbacks, signal);
}

export async function resumeAgent(
	threadId: string,
	approved: boolean,
	apiKey: string,
	callbacks: StreamCallbacks,
	signal: AbortSignal,
	refinement?: string,
	personId?: number,
	proposal?: ProjectProposal | null,
	companyId?: string,
	companyName?: string,
	// reconciliationAnswer: for RECONCILIATION_QUESTION interrupts (reconcile_sow reads `state.answer`)
	reconciliationAnswer?: string,
	createPlaceholdersEnabled?: boolean
): Promise<void> {
	const runId = SimpleUUID();
	const msgId = SimpleUUID();

	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (apiKey) headers['x-forecast-api-key'] = apiKey;
	if (personId != null) headers['x-person-id'] = String(personId);
	// Tenant headers for telemetry. Omit entirely when unavailable —
	// the gateway treats absent and empty headers differently.
	if (companyId) headers['X-Forecast-Company-Id'] = companyId;
	if (companyName) headers['X-Forecast-Company-Name'] = companyName;
	headers['x-agent-id'] = 'project_planning';

	// state becomes the interrupt resume value — backend routing:
	//   reconciliationAnswer → {answer} read by reconcile_sow interrupt
	//   refinement           → {approved:false, refinement} read by await_approval
	//   else                 → {approved, proposal?, create_placeholders_enabled?} read by await_approval
	const state = reconciliationAnswer
		? {answer: reconciliationAnswer}
		: refinement
		? {approved: false, refinement}
		: {
				approved,
				...(proposal ? {proposal} : {}),
				...(createPlaceholdersEnabled !== undefined ? {create_placeholders_enabled: createPlaceholdersEnabled} : {}),
		  };
	const userContent = reconciliationAnswer ?? refinement ?? (approved ? 'Approved' : 'Cancelled');

	const response = await fetch(`${AGENT_URL}/invocations`, {
		method: 'POST',
		headers,
		credentials: 'include',
		signal,
		body: JSON.stringify({
			api_key: apiKey,
			thread_id: threadId,
			run_id: runId,
			state,
			messages: [{id: msgId, role: 'user', content: userContent}],
			tools: [],
			context: [],
			forwardedProps: {agent_id: 'project_planning'},
		}),
	});

	if (!response.ok) {
		const text = await response.text().catch(() => '');
		callbacks.onError(`Server error ${response.status}: ${text.slice(0, 200)}`);
		return;
	}

	await parseSSEStream(response, callbacks, signal);
}
